/**
 * Signing in to the desktop app through the browser — what Google sign-in needs there: Google does
 * not accept the app window's origin (tauri.localhost), so the app sends the person to the website.
 *
 *   1. The app picks a random state, keeps it for 10 minutes, and opens
 *      <site>/auth/desktop?state=<state> in the browser.
 *   2. The website signs the person in (Google or password), makes a one-time handoff code — the
 *      same short-lived, single-use code "View on the web" uses the other way — and opens
 *      devpulse://signed-in?code=<code>&state=<state>.
 *   3. The app takes the link only with the state it is waiting for (a link anyone else made, to sign
 *      the app in to their account, is ignored), redeems the code and is signed in.
 */

import { api } from './api'
import { saveSession } from './auth'
import { openExternal, siteUrl } from './desktop'

const STATE_KEY = 'devpulse.desktopSignIn'
const TTL_MS = 10 * 60_000

interface Pending {
  state: string
  at: number
}

function pending(): Pending | null {
  try {
    const value = JSON.parse(localStorage.getItem(STATE_KEY) ?? 'null') as Pending | null
    return value && typeof value.state === 'string' && Date.now() - value.at < TTL_MS ? value : null
  } catch {
    return null
  }
}

/** The link the website hands back, or null when the address is not one. */
export function signedInLink(url: string): { code: string; state: string } | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol !== 'devpulse:' || parsed.hostname !== 'signed-in') return null
  const code = parsed.searchParams.get('code') ?? ''
  const state = parsed.searchParams.get('state') ?? ''
  return /^[A-Za-z0-9_-]{8,200}$/.test(code) && /^[A-Za-z0-9_-]{16,100}$/.test(state) ? { code, state } : null
}

/** Opens the website to sign in there; the app waits for the link back. */
export async function startDesktopSignIn(): Promise<void> {
  const bytes = crypto.getRandomValues(new Uint8Array(24))
  const state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify({ state, at: Date.now() } satisfies Pending))
  } catch {
    // No storage, no way to check the answer: the sign-in cannot finish here.
  }
  await openExternal(`${siteUrl()}/auth/desktop?state=${state}`)
}

/**
 * The link back from the website: signs the app in when it carries the state the app is waiting
 * for. True when it did.
 */
export async function acceptSignedInLink(url: string): Promise<boolean> {
  const link = signedInLink(url)
  const waiting = pending()
  if (!link || !waiting || link.state !== waiting.state) return false
  try {
    localStorage.removeItem(STATE_KEY)
  } catch {
    // Used once either way: the code is single-use on the server.
  }
  saveSession(await api.auth.redeemHandoff(link.code))
  return true
}

/** The page on the website: the address the app is sent back to with the code. */
export function backToApp(code: string, state: string): string {
  return `devpulse://signed-in?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
}
